import { useEffect, useState } from "react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import RotaCalendar from "@/components/RotaCalendar";
import ShiftQuickAddModal from "@/components/ShiftQuickAddModal";
import BulkShiftModal from "@/components/BulkShiftModal";
import { fmtISODate, fmtDate } from "@/lib/dates";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, AlertTriangle, Check, X, CalendarPlus, Loader2, Send, CheckCheck, CircleAlert, Trash2 } from "lucide-react";

// (Previously DELETABLE_STATUSES guarded the trash icon; deletion is now
// permitted for all statuses — confirmed shifts require an audit reason
// prompt enforced by the backend.)

const ROTA_DELETERS = new Set(["registered_manager", "administrator", "nominated_individual", "super_admin"]);

function DeleteWeekButton({ weekStart, reload }) {
  const { user } = useAuth();
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!user || !ROTA_DELETERS.has(user.role)) return null;

  const close = () => { setConfirming(false); setReason(""); setAck(false); setBusy(false); setError(""); };

  const onDelete = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/rota/delete-week", { week_start: weekStart, confirm: true, reason });
      close();
      reload();
      alert(`Rota deleted: ${data.shifts_deleted} shifts removed · ${data.workers_notified} worker(s) notified.`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "rota_has_worked_visits") {
        setError(detail.message);
      } else {
        setError(typeof detail === "string" ? detail : e.message);
      }
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setConfirming(true)}
        data-testid="delete-week-button"
        className="inline-flex items-center gap-2 border border-red-300 text-red-700 hover:bg-red-50 px-3 py-2 rounded-md text-sm font-medium"
      >
        <Trash2 className="h-4 w-4" /> Delete week
      </button>
      {confirming && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="delete-week-modal">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="font-heading text-lg font-bold text-slate-900">Delete rota for week of {weekStart}?</h3>
            <p className="mt-2 text-sm text-slate-700">
              This will remove every shift in the week and notify each assigned worker. Worked visits (Jibble check-ins or recorded notes) will block deletion.
            </p>
            <div className="mt-4">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Reason (optional)</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                data-testid="delete-week-reason"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                placeholder="e.g. Rota rebuilt due to staffing change"
              />
            </div>
            <label className="mt-3 flex items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={ack}
                onChange={(e) => setAck(e.target.checked)}
                data-testid="delete-week-ack"
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500/30"
              />
              I understand this action cannot be undone.
            </label>
            {error && <p className="mt-3 text-sm bg-red-50 border border-red-200 text-red-800 rounded px-3 py-2" data-testid="delete-week-error">{error}</p>}
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={close} className="px-4 py-2 text-sm font-medium rounded-md border border-slate-300 bg-white hover:bg-slate-50" data-testid="delete-week-cancel">Cancel</button>
              <button
                onClick={onDelete}
                disabled={busy || !ack}
                data-testid="delete-week-confirm"
                className="px-4 py-2 text-sm font-semibold rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Delete rota
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const VIEWS = [
  { key: "calendar", label: "Calendar" },
  { key: "week", label: "Week" },
  { key: "staff", label: "By Staff" },
  { key: "service_user", label: "By Service User" },
  { key: "unallocated", label: "Unallocated" },
];

function _nextDateISO(iso) {
  if (!iso) return "";
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

function _durationLabel(start, end, overnight) {
  const [sh, sm] = (start || "00:00").split(":").map(Number);
  const [eh, em] = (end || "00:00").split(":").map(Number);
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (overnight || mins <= 0) mins += 24 * 60;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

function isoMonday(d = new Date()) {
  // Anchor on the London-local date so the rota week never shifts across the
  // midnight UTC boundary when the user is in BST.
  const londonIso = fmtISODate(d) || new Date(d).toISOString().slice(0, 10);
  const date = new Date(`${londonIso}T12:00:00Z`); // noon UTC = always same calendar day in London
  const day = date.getUTCDay(); // 0..6 (Sun..Sat)
  const diff = (day === 0 ? -6 : 1) - day;
  date.setUTCDate(date.getUTCDate() + diff);
  return date.toISOString().slice(0, 10);
}

export default function Rota() {
  const { user } = useAuth();
  const isMgmt = ["registered_manager", "deputy_manager", "compliance_manager", "nominated_individual", "administrator", "team_leader", "super_admin"].includes(user.role);
  const isWorker = ["care_worker", "bank_worker"].includes(user.role);

  const [shifts, setShifts] = useState([]);
  const [users, setUsers] = useState({});
  const [staff, setStaff] = useState({});
  // Deep-link support: /rota?su=<id> or /rota?worker=<id> opens the Calendar
  // directly on that entity. We read the query string once at mount.
  const _initSearch = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const _initSu = _initSearch.get("su");
  const _initWorker = _initSearch.get("worker");
  const initialPivot = _initWorker ? "worker" : "service_user";
  const initialEntityId = _initWorker || _initSu || null;
  const [view, setView] = useState(isMgmt ? "calendar" : "week");
  const [creating, setCreating] = useState(false);
  const [creatingInitial, setCreatingInitial] = useState(null);
  const [calendarWeek, setCalendarWeek] = useState(isoMonday());
  const [unallocated, setUnallocated] = useState([]);
  // Quick-add modal opened by Calendar slot click.
  const [quickAdd, setQuickAdd] = useState(null); // { pivot, lockedEntity, lockedDate, initialStart, initialEnd }
  // Edit modal opened by clicking an existing shift block.
  const [editingShift, setEditingShift] = useState(null); // { pivot, lockedEntity, shift }
  // Bulk-add modal.
  const [bulkAdd, setBulkAdd] = useState(null); // { pivot, lockedEntity, defaultStartDate }

  const load = async () => {
    const { data } = await api.get("/shifts");
    setShifts(data.items || []);
    if (isMgmt) {
      const u = await api.get("/dashboard/dbs").catch(() => null); // warm
      const una = await api.get("/shifts/unallocated").catch(() => ({ data: { items: [] } }));
      setUnallocated(una.data.items || []);
    }
  };

  useEffect(() => {
    load();
    api.get("/service-users").then(({ data }) => {
      const m = {}; (data.items || []).forEach((s) => (m[s.id] = s)); setUsers(m);
    }).catch(() => {});
    if (isMgmt || user.role === "team_leader") {
      api.get("/staff").then(({ data }) => {
        const m = {}; (data.items || []).forEach((s) => (m[s.id] = s)); setStaff(m);
      }).catch(() => {});
    }
    // eslint-disable-next-line
  }, [user.role]);

  const groupBy = (arr, key) => arr.reduce((acc, s) => {
    const k = typeof key === "function" ? key(s) : s[key];
    (acc[k] = acc[k] || []).push(s); return acc;
  }, {});

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">{isWorker ? "My rota" : "Rota"}</h1>
          <p className="text-sm text-slate-600">Conflict-checked shift planning · Worker accept/decline · 11h rest &amp; 48h cap enforced.</p>
        </div>
        {isMgmt && (
          <button onClick={() => { setCreatingInitial(null); setCreating((v) => !v); }} data-testid="new-shift-button" className="inline-flex items-center gap-2 bg-[#0D7377] hover:bg-[#0A5C5F] text-white px-4 py-2 rounded-md font-medium min-h-[44px]">
            <CalendarPlus className="h-4 w-4" /> New shift
          </button>
        )}
      </div>

      {creating && isMgmt && (
        <ShiftForm
          staff={Object.values(staff)}
          users={Object.values(users)}
          onClose={() => { setCreating(false); setCreatingInitial(null); }}
          reload={load}
          initial={creatingInitial}
        />
      )}

      {isMgmt && <PublishWeekPanel reload={load} />}
      {isWorker && <ConfirmWeekPanel reload={load} />}

      {/* View tabs */}
      <div className="border-b border-slate-200 flex gap-1 overflow-x-auto" data-testid="rota-view-tabs">
        {VIEWS.filter((v) => isMgmt || !["unallocated", "calendar"].includes(v.key)).map((v) => (
          <button key={v.key} onClick={() => setView(v.key)} data-testid={`rota-view-${v.key}`} className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 ${view === v.key ? "text-[#0D7377] border-[#0D7377]" : "text-slate-600 border-transparent hover:text-slate-900"}`}>
            {v.label} {v.key === "unallocated" && unallocated.length > 0 && <span className="ml-1 text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full">{unallocated.length}</span>}
          </button>
        ))}
      </div>

      {view === "calendar" && isMgmt && (
        <RotaCalendar
          shifts={shifts}
          staff={Object.values(staff)}
          users={Object.values(users)}
          weekStart={calendarWeek}
          onWeekChange={setCalendarWeek}
          defaultPivot={initialPivot}
          defaultEntityId={initialEntityId}
          onSlotClick={(ctx) => setQuickAdd(ctx)}
          onShiftClick={(sh) => {
            // Open edit-mode modal. Resolve which entity is "locked" based on
            // current pivot — for SU pivot the SU stays locked, worker for worker pivot.
            const pivotEntity = initialPivot === "worker"
              ? { id: sh.worker_id, label: staff[sh.worker_id]?.full_name || "Worker", sub: (staff[sh.worker_id]?.role || "").replace(/_/g, " ") }
              : (() => {
                  const suId = sh.visit_slots?.[0]?.service_user_id;
                  return { id: suId, label: users[suId]?.full_name || "Service user", sub: users[suId]?.address?.line1 || "" };
                })();
            setEditingShift({ pivot: initialPivot, lockedEntity: pivotEntity, shift: sh });
          }}
          onShiftDrop={async ({ shiftId, newDate, newStart }) => {
            const orig = shifts.find((s) => s.id === shiftId);
            if (!orig) return;
            // Compute new end keeping the same duration as the original.
            const [sh1, sm1] = orig.start_time.split(":").map(Number);
            const [eh1, em1] = orig.end_time.split(":").map(Number);
            const baseStart = sh1 * 60 + sm1;
            const baseEnd = eh1 * 60 + em1;
            let durMins = baseEnd - baseStart;
            if (orig.overnight || durMins <= 0) durMins += 24 * 60;
            const [nsH, nsM] = newStart.split(":").map(Number);
            const newEndMins = (nsH * 60 + nsM) + durMins;
            const newEnd = `${String(Math.floor(newEndMins / 60) % 24).padStart(2, "0")}:${String(newEndMins % 60).padStart(2, "0")}`;
            // Optimistic update — splice immediately.
            const optimistic = { ...orig, date: newDate, start_time: newStart, end_time: newEnd };
            setShifts((prev) => prev.map((s) => (s.id === shiftId ? optimistic : s)));
            try {
              const { data } = await api.patch(`/shifts/${shiftId}`, { date: newDate, start_time: newStart, end_time: newEnd });
              setShifts((prev) => prev.map((s) => (s.id === shiftId ? (data.shift || s) : s)));
            } catch (err) {
              // Rollback on failure.
              setShifts((prev) => prev.map((s) => (s.id === shiftId ? orig : s)));
              const detail = err.response?.data?.detail;
              alert(typeof detail === "string" ? detail : detail?.message || "Could not move shift. It may conflict — edit it directly to confirm.");
            }
          }}
          onDuplicateWeek={async (sourceWeek) => {
            const target = (() => { const d = new Date(`${sourceWeek}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + 7); return d.toISOString().slice(0, 10); })();
            if (!window.confirm(`Duplicate every published shift from week of ${sourceWeek} to week of ${target}?`)) return;
            try {
              const { data } = await api.post("/shifts/duplicate-week", { source_week_start: sourceWeek, target_week_start: target });
              load();
              setCalendarWeek(target);
              const msg = `${data.created_count} shifts copied to ${target}.` + (data.skipped_count ? ` ${data.skipped_count} skipped (conflicts).` : "");
              alert(msg);
            } catch (err) {
              alert(err.response?.data?.detail || "Could not duplicate week.");
            }
          }}
          onBulkAdd={() => {
            // Resolve the entity in focus from the current pivot + selectedId reported by the calendar.
            // The calendar emits the focused entity in the slot/shift callbacks, but for the toolbar
            // button we infer it from initial state. We pass through whatever was deep-linked or the
            // first entity available.
            const all = initialPivot === "worker"
              ? Object.values(staff).filter((s) => ["care_worker", "bank_worker", "team_leader", "registered_manager", "deputy_manager"].includes(s.role))
              : Object.values(users);
            const entity = all.find((e) => e.id === initialEntityId) || all[0];
            if (!entity) { alert("No entities to bulk-add against."); return; }
            setBulkAdd({
              pivot: initialPivot,
              lockedEntity: { id: entity.id, label: entity.full_name, sub: (entity.role || entity.address?.line1 || "").replace(/_/g, " ") },
              defaultStartDate: calendarWeek,
            });
          }}
        />
      )}
      {quickAdd && (
        <ShiftQuickAddModal
          pivot={quickAdd.pivot}
          lockedEntity={quickAdd.lockedEntity}
          lockedDate={quickAdd.lockedDate}
          initialStart={quickAdd.initialStart}
          initialEnd={quickAdd.initialEnd}
          staff={Object.values(staff)}
          users={Object.values(users)}
          onClose={() => setQuickAdd(null)}
          onCreated={(newShift) => {
            const enriched = { ...newShift, worker_name: newShift.worker_name || staff[newShift.worker_id]?.full_name || "Worker" };
            setShifts((prev) => [...prev.filter((s) => s.id !== enriched.id), enriched]);
            setQuickAdd(null);
          }}
        />
      )}
      {editingShift && (
        <ShiftQuickAddModal
          pivot={editingShift.pivot}
          lockedEntity={editingShift.lockedEntity}
          lockedDate={editingShift.shift.date}
          initialStart={editingShift.shift.start_time}
          initialEnd={editingShift.shift.end_time}
          existing={editingShift.shift}
          staff={Object.values(staff)}
          users={Object.values(users)}
          onClose={() => setEditingShift(null)}
          onUpdated={(updated) => {
            const enriched = { ...updated, worker_name: updated.worker_name || staff[updated.worker_id]?.full_name || "Worker" };
            setShifts((prev) => prev.map((s) => (s.id === enriched.id ? enriched : s)));
            setEditingShift(null);
          }}
          onDeleted={(id) => {
            setShifts((prev) => prev.filter((s) => s.id !== id));
            setEditingShift(null);
          }}
        />
      )}
      {bulkAdd && (
        <BulkShiftModal
          pivot={bulkAdd.pivot}
          lockedEntity={bulkAdd.lockedEntity}
          staff={Object.values(staff)}
          users={Object.values(users)}
          defaultStartDate={bulkAdd.defaultStartDate}
          onClose={() => setBulkAdd(null)}
          onCreated={() => { load(); }}
        />
      )}
      {view === "week" && <WeekView shifts={shifts} staff={staff} users={users} reload={load} isWorker={isWorker} />}
      {view === "staff" && <ByStaffView shifts={shifts} staff={staff} users={users} />}
      {view === "service_user" && <BySuView shifts={shifts} staff={staff} users={users} />}
      {view === "unallocated" && <UnallocatedView shifts={unallocated} staff={staff} users={users} reload={load} />}
    </div>
  );
}

function WeekView({ shifts, staff, users, reload, isWorker }) {
  // Care-worker view is action-oriented: what needs my attention comes first,
  // then what's coming up (soonest first), then what's already happened
  // (most recent past at top). Managers still see the natural Monday→Sunday
  // chronological layout.
  const todayISO = new Date().toISOString().slice(0, 10);
  const byDate = shifts.reduce((acc, s) => { (acc[s.date] = acc[s.date] || []).push(s); return acc; }, {});
  const allDates = Object.keys(byDate);
  let dates;
  if (isWorker) {
    const pendingDates = new Set(
      allDates.filter((d) => byDate[d].some((s) => (s.confirmation_status ?? "pending") === "pending" && d >= todayISO)),
    );
    const upcoming = allDates.filter((d) => d >= todayISO && !pendingDates.has(d)).sort();
    const pendingList = [...pendingDates].sort();
    const past = allDates.filter((d) => d < todayISO).sort().reverse();
    dates = [...pendingList, ...upcoming, ...past];
  } else {
    dates = allDates.sort();
  }
  const relativeLabel = (d) => {
    if (!isWorker) return null;
    const diff = Math.round((new Date(`${d}T12:00:00Z`) - new Date(`${todayISO}T12:00:00Z`)) / (1000 * 60 * 60 * 24));
    if (diff === 0) return "Today";
    if (diff === 1) return "Tomorrow";
    if (diff === -1) return "Yesterday";
    if (diff > 1 && diff <= 7) return `In ${diff} days`;
    if (diff < -1 && diff >= -7) return `${-diff} days ago`;
    return null;
  };
  return (
    <div className="space-y-4">
      {dates.map((d) => {
        const dayShifts = byDate[d].slice().sort((a, b) => a.start_time.localeCompare(b.start_time));
        const hasPending = isWorker && d >= todayISO && dayShifts.some((s) => (s.confirmation_status ?? "pending") === "pending");
        const relLabel = relativeLabel(d);
        return (
          <div key={d} className={`bg-white rounded-lg border shadow-sm ${hasPending ? "border-amber-300 ring-1 ring-amber-100" : "border-slate-200"}`}>
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-baseline gap-2 flex-wrap">
                {relLabel && (
                  <span className={`text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded ${
                    relLabel === "Today" ? "bg-emerald-100 text-emerald-800" :
                    relLabel === "Tomorrow" ? "bg-sky-100 text-sky-800" :
                    relLabel.includes("ago") || relLabel === "Yesterday" ? "bg-slate-100 text-slate-600" :
                    "bg-amber-50 text-amber-800"
                  }`}>{relLabel}</span>
                )}
                <p className="font-heading font-semibold text-slate-900">{new Intl.DateTimeFormat("en-GB", { weekday: "long", day: "numeric", month: "long", timeZone: "Europe/London" }).format(new Date(`${d}T12:00:00Z`))}</p>
                {hasPending && <span className="text-xs bg-amber-100 text-amber-900 rounded px-2 py-0.5 font-semibold">Needs your action</span>}
              </div>
              <p className="text-xs text-slate-500">{dayShifts.length} shift{dayShifts.length !== 1 ? "s" : ""}</p>
            </div>
            <ul className="divide-y divide-slate-100">
              {dayShifts.map((s) => (
                <ShiftRow key={s.id} shift={s} staff={staff} users={users} reload={reload} isWorker={isWorker} />
              ))}
            </ul>
          </div>
        );
      })}
      {dates.length === 0 && <p className="text-sm text-slate-500">No shifts scheduled.</p>}
    </div>
  );
}

function ShiftRow({ shift, staff, users, reload, isWorker }) {
  const [busy, setBusy] = useState(false);
  const [concernNote, setConcernNote] = useState("");
  const [showConcern, setShowConcern] = useState(false);

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/shifts/${shift.id}/confirm`, { note: "" });
      reload();
    } finally {
      setBusy(false);
    }
  };

  const flagConcern = async () => {
    if (!concernNote.trim()) {
      setShowConcern(true);
      return;
    }
    setBusy(true);
    try {
      await api.post(`/shifts/${shift.id}/flag-concern`, { note: concernNote });
      reload();
    } finally {
      setBusy(false);
      setShowConcern(false);
      setConcernNote("");
    }
  };

  const confirmStatus = shift.confirmation_status;
  const hasSlots = (shift.visit_slots || []).length > 0;
  const isMgmt = !isWorker;
  const needsReassign =
    isMgmt && (confirmStatus === "concern_flagged" || shift.status === "declined" || !shift.worker_id);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignTo, setReassignTo] = useState("");
  const [reassignReason, setReassignReason] = useState("");
  const [reassignErr, setReassignErr] = useState("");

  const submitReassign = async () => {
    if (!reassignTo) {
      setReassignErr("Pick a worker to reassign to.");
      return;
    }
    setBusy(true);
    setReassignErr("");
    try {
      await api.post(`/shifts/${shift.id}/reassign`, {
        new_worker_id: reassignTo,
        reason: reassignReason,
      });
      setReassignOpen(false);
      setReassignTo("");
      setReassignReason("");
      reload();
    } catch (e) {
      setReassignErr(e?.response?.data?.detail || "Failed to reassign");
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="px-5 py-3" data-testid={`shift-row-${shift.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-900">
            {staff[shift.worker_id]?.full_name || (isWorker ? "" : "Unassigned")}
            {staff[shift.worker_id]?.full_name && " · "}
            <span className="text-slate-700">{shift.start_time}–{shift.end_time}</span>
            {shift.duration_minutes != null && (
              <span className="ml-2 text-xs text-slate-500">({Math.floor(shift.duration_minutes / 60)}h {shift.duration_minutes % 60 ? `${shift.duration_minutes % 60}m` : ""})</span>
            )}
          </p>
          {hasSlots ? (
            <ul className="mt-1.5 space-y-1" data-testid={`shift-slots-${shift.id}`}>
              {shift.visit_slots.map((v, i) => {
                const name = v.service_user_name || users[v.service_user_id]?.full_name || "Service user";
                const addr = v.service_user_address || users[v.service_user_id]?.address;
                const addrLine = addr ? [addr.line1, addr.city, addr.postcode].filter(Boolean).join(", ") : "";
                return (
                  <li key={i} className="text-xs text-slate-700 inline-flex items-start gap-1.5" data-testid={`shift-slot-${shift.id}-${i}`}>
                    <span className="font-semibold text-slate-900">{name}</span>
                    {v.is_key_worker && <span className="bg-emerald-50 text-emerald-700 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded">Key worker</span>}
                    <span className="text-slate-500">·</span>
                    <span className="tabular-nums">{v.planned_start}–{v.planned_end}</span>
                    {addrLine && <><span className="text-slate-500">·</span><span className="text-slate-500">{addrLine}</span></>}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-xs text-slate-500 italic mt-1">No visits assigned to this shift yet.</p>
          )}
          {(shift.conflicts_at_creation || []).filter((c) => c.severity !== "low").length > 0 && (
            <p className="mt-1 text-xs text-amber-700 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {shift.conflicts_at_creation.length} conflict{shift.conflicts_at_creation.length !== 1 ? "s" : ""} flagged · {shift.override_reason ? `Override: ${shift.override_reason}` : "no override reason"}</p>
          )}
          {confirmStatus === "concern_flagged" && shift.confirmation_note && (
            <p className="mt-1 text-xs text-rose-700 inline-flex items-center gap-1"><CircleAlert className="h-3 w-3" /> Concern: {shift.confirmation_note}</p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
          <div className="flex items-center gap-2">
            {shift.shift_type && <StatusBadge status="active" label={shift.shift_type} />}
            {confirmStatus === "confirmed" ? (
              <StatusBadge status="active" label="Confirmed" />
            ) : confirmStatus === "concern_flagged" ? (
              <StatusBadge status="overdue" label="Concern" />
            ) : (
              <StatusBadge status={shift.status} />
            )}
          </div>
          {isWorker && (shift.status === "published" || shift.status === "draft") && confirmStatus !== "confirmed" && (
            <div className="flex items-center gap-2">
              <button disabled={busy} onClick={confirm} data-testid={`shift-confirm-${shift.id}`} className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded-md font-medium inline-flex items-center gap-1">
                <Check className="h-3 w-3" /> Confirm
              </button>
              <button disabled={busy} onClick={flagConcern} data-testid={`shift-flag-${shift.id}`} className="px-3 py-1 bg-amber-600 hover:bg-amber-700 text-white text-xs rounded-md font-medium inline-flex items-center gap-1">
                <X className="h-3 w-3" /> Flag concern
              </button>
            </div>
          )}
          {needsReassign && (
            <button
              disabled={busy}
              onClick={() => setReassignOpen((v) => !v)}
              data-testid={`shift-reassign-${shift.id}`}
              className="px-3 py-1 bg-[#0D7377] hover:bg-[#0A5C5F] text-white text-xs rounded-md font-medium inline-flex items-center gap-1"
            >
              {shift.worker_id ? "Reassign…" : "Allocate…"}
            </button>
          )}
          {isMgmt && (
            <button
              disabled={busy}
              onClick={async () => {
                const isConfirmed = shift.confirmation_status === "confirmed";
                let reason = "";
                if (isConfirmed) {
                  reason = (window.prompt(
                    `This shift on ${shift.date} ${shift.start_time}–${shift.end_time} has already been ACCEPTED by the worker.\n\nEnter a reason for deletion (min 5 chars, will be saved to the audit log and shared with the worker):`,
                    "",
                  ) || "").trim();
                  if (!reason) return;
                  if (reason.length < 5) {
                    alert("Reason must be at least 5 characters.");
                    return;
                  }
                } else {
                  if (!window.confirm(`Delete shift on ${shift.date} ${shift.start_time}–${shift.end_time}?\n\nThis cannot be undone. The assigned worker will be notified.`)) return;
                }
                setBusy(true);
                try {
                  const qs = reason ? `?reason=${encodeURIComponent(reason)}` : "";
                  await api.delete(`/shifts/${shift.id}${qs}`);
                  reload();
                } catch (e) {
                  const detail = e?.response?.data?.detail;
                  alert(typeof detail === "string" ? detail : detail?.message || "Could not delete shift");
                } finally {
                  setBusy(false);
                }
              }}
              data-testid={`shift-delete-${shift.id}`}
              className="p-1.5 text-rose-700 hover:bg-rose-50 rounded-md"
              aria-label="Delete shift"
              title="Delete shift"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
      {showConcern && (
        <div className="mt-2 flex gap-2">
          <input value={concernNote} onChange={(e) => setConcernNote(e.target.value)} placeholder="Why can't you do this shift?…" className="flex-1 px-3 py-1.5 border border-slate-300 rounded-md text-sm" data-testid={`shift-concern-note-${shift.id}`} />
          <button disabled={busy} onClick={flagConcern} className="px-3 py-1.5 bg-amber-600 text-white text-xs rounded-md font-medium">Send concern</button>
        </div>
      )}
      {reassignOpen && (
        <div className="mt-3 p-3 border border-slate-200 bg-slate-50 rounded-md space-y-2" data-testid={`shift-reassign-form-${shift.id}`}>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">{shift.worker_id ? "Reassign to" : "Allocate to"}</p>
          <select
            autoFocus
            value={reassignTo}
            onChange={(e) => setReassignTo(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
            data-testid={`shift-reassign-select-${shift.id}`}
          >
            <option value="">Pick a worker…</option>
            {Object.values(staff)
              .filter((u) => u.id !== shift.worker_id && (u.status || "active") === "active" && ["care_worker", "bank_worker", "team_leader"].includes(u.role))
              .sort((a, b) => (a.full_name || "").localeCompare(b.full_name || ""))
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name} ({u.role.replace("_", " ")})
                </option>
              ))}
          </select>
          <input
            value={reassignReason}
            onChange={(e) => setReassignReason(e.target.value)}
            placeholder="Reason (e.g. covering for declined shift)"
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
            data-testid={`shift-reassign-reason-${shift.id}`}
          />
          {reassignErr && <p className="text-xs text-rose-700">{reassignErr}</p>}
          <div className="flex justify-end gap-2">
            <button onClick={() => { setReassignOpen(false); setReassignTo(""); setReassignErr(""); }} className="text-xs px-3 py-1.5 rounded-md border border-slate-300 bg-white text-slate-700 font-medium">
              Cancel
            </button>
            <button
              disabled={busy || !reassignTo}
              onClick={submitReassign}
              data-testid={`shift-reassign-confirm-${shift.id}`}
              className="text-xs px-3 py-1.5 rounded-md bg-[#0D7377] hover:bg-[#0A5C5F] text-white font-semibold disabled:opacity-60"
            >
              {busy ? "Saving…" : "Reassign"}
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

function ByStaffView({ shifts, staff, users }) {
  const grouped = shifts.reduce((acc, s) => { (acc[s.worker_id] = acc[s.worker_id] || []).push(s); return acc; }, {});
  return (
    <div className="space-y-3">
      {Object.entries(grouped).map(([wid, list]) => {
        const totalHrs = list.reduce((acc, s) => acc + (parseInt(s.end_time, 10) - parseInt(s.start_time, 10)), 0);
        return (
          <div key={wid} className="bg-white rounded-lg border border-slate-200 shadow-sm p-4">
            <div className="flex items-center justify-between">
              <p className="font-medium text-slate-900">{staff[wid]?.full_name || "Unknown"}</p>
              <p className="text-xs text-slate-500">{list.length} shifts · ~{totalHrs}h</p>
            </div>
            <ul className="mt-2 text-sm text-slate-700 space-y-1">
              {list.sort((a, b) => (a.date + a.start_time).localeCompare(b.date + b.start_time)).map((s) => (
                <li key={s.id} className="flex items-center justify-between">
                  <span>{s.date} · {s.start_time}–{s.end_time}</span>
                  <StatusBadge status={s.status} />
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function BySuView({ shifts, staff, users }) {
  const grouped = {};
  shifts.forEach((s) => (s.visit_slots || []).forEach((v) => {
    (grouped[v.service_user_id] = grouped[v.service_user_id] || []).push({ ...s, slot: v });
  }));
  return (
    <div className="space-y-3">
      {Object.entries(grouped).map(([sid, list]) => (
        <div key={sid} className="bg-white rounded-lg border border-slate-200 shadow-sm p-4">
          <p className="font-medium text-slate-900">{users[sid]?.full_name || "Unknown"}</p>
          <ul className="mt-2 text-sm text-slate-700 space-y-1">
            {list.map((s, i) => (
              <li key={i} className="flex items-center justify-between">
                <span>{s.date} · {s.slot.planned_start}–{s.slot.planned_end} · {staff[s.worker_id]?.full_name}</span>
                <StatusBadge status={s.status} />
              </li>
            ))}
          </ul>
        </div>
      ))}
      {Object.keys(grouped).length === 0 && <p className="text-sm text-slate-500">No service-user-linked shifts.</p>}
    </div>
  );
}

function UnallocatedView({ shifts, staff, users, reload }) {
  return (
    <div className="space-y-3">
      <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-900">
        These shifts were declined, flagged with a concern, or have no assigned worker. They need urgent cover.
      </div>
      <ul className="bg-white rounded-lg border border-red-200 shadow-sm divide-y divide-slate-100">
        {shifts.map((s) => (
          <ShiftRow key={s.id} shift={s} staff={staff} users={users} reload={reload} isWorker={false} />
        ))}
        {shifts.length === 0 && <li className="px-5 py-6 text-sm text-slate-500">No unallocated shifts. Great work.</li>}
      </ul>
    </div>
  );
}

function ShiftForm({ staff, users, onClose, reload, initial }) {
  const [form, setForm] = useState({
    worker_id: "", date: fmtISODate(new Date()),
    start_time: "08:00", end_time: "16:00", shift_type: "Regular Visit", status: "published",
    overnight: false,
    visit_slots: [],
    ...(initial || {}),
  });
  const [conflicts, setConflicts] = useState([]);
  const [override, setOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const checkConflicts = async () => {
    if (!form.worker_id || !form.date) return;
    try {
      const { data } = await api.post("/shifts/check-conflicts", { worker_id: form.worker_id, date: form.date, start_time: form.start_time, end_time: form.end_time, overnight: form.overnight });
      setConflicts(data.conflicts || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { checkConflicts(); /* eslint-disable-next-line */ }, [form.worker_id, form.date, form.start_time, form.end_time, form.overnight]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      // Validate visit slots
      for (const [i, v] of form.visit_slots.entries()) {
        if (!v.service_user_id) { setError(`Visit ${i + 1}: choose a service user.`); setBusy(false); return; }
        // For overnight shifts the slot times can cross midnight so we skip
        // the window check; for normal shifts they must fall within the window.
        if (!form.overnight) {
          if (v.planned_start < form.start_time || v.planned_end > form.end_time) {
            setError(`Visit ${i + 1}: planned time (${v.planned_start}–${v.planned_end}) must fall within the shift window (${form.start_time}–${form.end_time}).`);
            setBusy(false);
            return;
          }
          if (v.planned_end <= v.planned_start) {
            setError(`Visit ${i + 1}: end time must be after start time.`);
            setBusy(false);
            return;
          }
        }
      }
      const blocking = conflicts.filter((c) => ["high", "critical"].includes(c.severity));
      if (blocking.length && (!override || !overrideReason.trim())) {
        setError("Conflicts detected — tick override and add a reason to proceed.");
        setBusy(false);
        return;
      }
      await api.post("/shifts", { ...form, override_conflicts: override, override_reason: overrideReason });
      reload();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : detail?.message || "Failed");
    } finally { setBusy(false); }
  };

  const eligible = staff.filter((s) => ["care_worker", "bank_worker", "team_leader", "registered_manager", "deputy_manager"].includes(s.role));

  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 space-y-3" data-testid="shift-form">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <select required value={form.worker_id} onChange={(e) => setForm({ ...form, worker_id: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md" data-testid="shift-worker-select">
          <option value="">Worker…</option>
          {eligible.map((s) => <option key={s.id} value={s.id}>{s.full_name} ({s.role.replace(/_/g, " ")})</option>)}
        </select>
        <input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md" />
        <select value={form.shift_type} onChange={(e) => setForm({ ...form, shift_type: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md">
          {["Regular Visit", "Sleep-In", "Double-Up", "Cover Visit", "Training", "Standby", "Office"].map((t) => <option key={t}>{t}</option>)}
        </select>
        <input type="time" required value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md" data-testid="shift-start" />
        <input type="time" required value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md" data-testid="shift-end" />
        <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md">
          <option value="draft">Save as draft</option>
          <option value="published">Publish to worker</option>
        </select>
      </div>

      {/* Overnight / sleep-in toggle — surfaces the spanned dates clearly so RM doesn't second-guess. */}
      <label className="flex items-start gap-2 text-sm bg-slate-50 border border-slate-200 rounded-md px-3 py-2" data-testid="shift-overnight-row">
        <input
          type="checkbox"
          checked={!!form.overnight}
          onChange={(e) => setForm({
            ...form,
            overnight: e.target.checked,
            // Default to a sensible sleep-in window the first time it's flipped on.
            ...(e.target.checked && form.start_time === "08:00" && form.end_time === "16:00"
              ? { start_time: "22:00", end_time: "08:00", shift_type: "Sleep-In" }
              : {}),
          })}
          data-testid="shift-overnight-toggle"
          className="mt-1"
        />
        <div className="flex-1">
          <p className="font-medium text-slate-900">Overnight (sleep-in) — shift rolls into the next day</p>
          {form.overnight ? (
            <p className="text-xs text-emerald-800">
              Starts <strong>{form.start_time}</strong> on <strong>{fmtDate(form.date)}</strong> · ends <strong>{form.end_time}</strong> on <strong>{fmtDate(_nextDateISO(form.date))}</strong>
              {" "}({_durationLabel(form.start_time, form.end_time, true)}).
            </p>
          ) : (
            <p className="text-xs text-slate-500">Tick this when the end time is on the calendar day after the start time (e.g. 22:00 → 08:00).</p>
          )}
        </div>
      </label>

      <fieldset className="border border-slate-200 rounded-md p-3" data-testid="shift-visits-fieldset">
        <legend className="text-xs uppercase tracking-wider font-semibold text-slate-600 px-1">Visits in this shift</legend>
        <p className="text-xs text-slate-500 mb-2">Add one row per service user the worker will visit. Times must fall within the shift window.</p>
        {form.visit_slots.length === 0 && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 inline-flex items-center gap-1.5 mb-2" data-testid="visits-empty-warning">
            <AlertTriangle className="h-3 w-3" /> No visits added — the care worker won't see any service user on this shift.
          </p>
        )}
        <div className="space-y-2">
          {form.visit_slots.map((slot, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_auto] gap-2 items-center" data-testid={`visit-slot-row-${i}`}>
              <select
                required
                value={slot.service_user_id}
                onChange={(e) => {
                  const next = [...form.visit_slots];
                  next[i] = { ...next[i], service_user_id: e.target.value };
                  setForm({ ...form, visit_slots: next });
                }}
                className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                data-testid={`visit-slot-su-${i}`}
              >
                <option value="">Service user…</option>
                {users.map((su) => (
                  <option key={su.id} value={su.id}>
                    {su.full_name}{su.address?.line1 ? ` — ${su.address.line1}` : ""}
                  </option>
                ))}
              </select>
              <input
                type="time"
                required
                value={slot.planned_start}
                onChange={(e) => {
                  const next = [...form.visit_slots];
                  next[i] = { ...next[i], planned_start: e.target.value };
                  setForm({ ...form, visit_slots: next });
                }}
                className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                data-testid={`visit-slot-start-${i}`}
              />
              <input
                type="time"
                required
                value={slot.planned_end}
                onChange={(e) => {
                  const next = [...form.visit_slots];
                  next[i] = { ...next[i], planned_end: e.target.value };
                  setForm({ ...form, visit_slots: next });
                }}
                className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                data-testid={`visit-slot-end-${i}`}
              />
              <button
                type="button"
                onClick={() => setForm({ ...form, visit_slots: form.visit_slots.filter((_, idx) => idx !== i) })}
                className="p-2 text-rose-700 hover:bg-rose-50 rounded-md"
                aria-label="Remove visit"
                data-testid={`visit-slot-remove-${i}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setForm({
            ...form,
            visit_slots: [
              ...form.visit_slots,
              {
                service_user_id: "",
                planned_start: form.start_time,
                planned_end: form.end_time,
              },
            ],
          })}
          className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-[#0D7377] hover:underline"
          data-testid="add-visit-slot-button"
        >
          <Plus className="h-4 w-4" /> Add visit
        </button>
      </fieldset>

      {conflicts.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 space-y-2" data-testid="shift-conflicts">
          <p className="text-sm font-semibold text-amber-900 flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> Conflicts detected</p>
          <ul className="text-xs text-amber-900 space-y-1">
            {conflicts.map((c, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <span className={`mt-0.5 h-2 w-2 rounded-full ${c.severity === "critical" ? "bg-red-600" : c.severity === "high" ? "bg-amber-600" : "bg-blue-500"}`} />
                <span><strong className="uppercase text-[10px] tracking-wider">{c.severity}</strong> · {c.message}</span>
              </li>
            ))}
          </ul>
          {conflicts.some((c) => ["high", "critical"].includes(c.severity)) && (
            <div className="pt-2 border-t border-amber-200 space-y-1">
              <label className="flex items-start gap-2 text-xs text-amber-900">
                <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} data-testid="shift-override-checkbox" />
                <span>Override conflicts and proceed (your decision is audit-logged)</span>
              </label>
              {override && (
                <input required={override} placeholder="Reason for overriding" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} className="w-full px-3 py-1.5 border border-amber-300 rounded-md text-xs bg-white" data-testid="shift-override-reason" />
              )}
            </div>
          )}
        </div>
      )}

      {error && <p className="text-sm text-red-700">{error}</p>}

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="px-4 py-2 border border-slate-300 rounded-md">Cancel</button>
        <button type="submit" disabled={busy} data-testid="shift-submit-button" className="px-4 py-2 bg-[#0D7377] hover:bg-[#0A5C5F] text-white rounded-md font-medium inline-flex items-center gap-2">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save shift
        </button>
      </div>
    </form>
  );
}


function PublishWeekPanel({ reload }) {
  const [weekStart, setWeekStart] = useState(isoMonday());
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const loadStatus = async (ws) => {
    try {
      const { data } = await api.get(`/rota/confirmation-status?week_start=${ws}`);
      setStatus(data);
    } catch {
      setStatus(null);
    }
  };

  useEffect(() => {
    loadStatus(weekStart);
  }, [weekStart]);

  const publish = async () => {
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.post("/rota/publish-week", { week_start: weekStart });
      setMsg(
        data.published_count > 0
          ? `Published ${data.published_count} shift${data.published_count === 1 ? "" : "s"} · notified ${data.workers_notified} worker${data.workers_notified === 1 ? "" : "s"}.`
          : "No draft shifts to publish for this week.",
      );
      await loadStatus(weekStart);
      reload();
    } catch (e) {
      setMsg(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const t = status?.totals;

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5" data-testid="publish-week-panel">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div>
          <h3 className="font-heading text-base font-semibold text-slate-900">Publish &amp; confirmation</h3>
          <p className="text-xs text-slate-500">Publish a week's draft shifts and track worker confirmations.</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={weekStart}
            onChange={(e) => setWeekStart(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-md text-sm"
            data-testid="publish-week-date"
          />
          <button
            onClick={publish}
            disabled={busy}
            data-testid="publish-week-button"
            className="inline-flex items-center gap-2 bg-[#0D7377] hover:bg-[#0A5C5F] text-white px-4 py-2 rounded-md font-medium min-h-[40px]"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Publish week
          </button>
          <DeleteWeekButton weekStart={weekStart} reload={reload} />
        </div>
      </div>

      {msg && (
        <p className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-900 rounded px-2 py-1.5 mb-3" data-testid="publish-week-msg">
          {msg}
        </p>
      )}

      {t && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="confirmation-totals">
          <Kpi label="Workers" value={t.workers} testId="kpi-workers" />
          <Kpi label="Total shifts" value={t.total_shifts} testId="kpi-total-shifts" />
          <Kpi label="Confirmed" value={`${t.confirmed} (${t.confirmation_pct}%)`} tone="ok" testId="kpi-confirmed" />
          <Kpi
            label={t.concerns > 0 ? "Concerns" : "Pending"}
            value={t.concerns > 0 ? t.concerns : t.pending}
            tone={t.concerns > 0 ? "warn" : t.pending > 0 ? "amber" : "ok"}
            testId="kpi-attention"
          />
        </div>
      )}

      {status?.per_worker && status.per_worker.length > 0 && (
        <div className="mt-4 overflow-auto">
          <table className="w-full text-sm" data-testid="confirmation-per-worker">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="text-left py-2 px-3">Worker</th>
                <th className="text-right py-2 px-3">Shifts</th>
                <th className="text-right py-2 px-3">Confirmed</th>
                <th className="text-right py-2 px-3">Pending</th>
                <th className="text-right py-2 px-3">Concerns</th>
                <th className="text-right py-2 px-3">%</th>
              </tr>
            </thead>
            <tbody>
              {status.per_worker.map((r) => (
                <tr key={r.worker_id} className="border-b border-slate-100" data-testid={`confirmation-row-${r.worker_id}`}>
                  <td className="py-2 px-3 font-medium text-slate-900">{r.worker_name}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{r.total}</td>
                  <td className="py-2 px-3 text-right tabular-nums text-emerald-700">{r.confirmed}</td>
                  <td className="py-2 px-3 text-right tabular-nums text-amber-700">{r.pending}</td>
                  <td className="py-2 px-3 text-right tabular-nums text-rose-700">{r.concerns}</td>
                  <td className="py-2 px-3 text-right">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-md ${r.confirmation_pct >= 80 ? "bg-emerald-50 text-emerald-700" : r.confirmation_pct >= 40 ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-700"}`}>{r.confirmation_pct}%</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ConfirmWeekPanel({ reload }) {
  const [weekStart, setWeekStart] = useState(isoMonday());
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async (ws) => {
    try {
      const { data } = await api.get(`/rota/my-rota?week_start=${ws}`);
      setSummary(data.summary);
    } catch {
      setSummary(null);
    }
  };

  useEffect(() => {
    load(weekStart);
  }, [weekStart]);

  const confirmAll = async () => {
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.post("/rota/confirm-week", { week_start: weekStart });
      setMsg(
        data.shifts_confirmed > 0
          ? `Confirmed ${data.shifts_confirmed} shift${data.shifts_confirmed === 1 ? "" : "s"}.`
          : "No pending shifts to confirm this week.",
      );
      await load(weekStart);
      reload();
    } catch (e) {
      setMsg(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!summary) return null;
  const allConfirmed = summary.pending === 0 && summary.concerns === 0 && summary.confirmed === summary.total;

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4" data-testid="confirm-week-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={weekStart}
            onChange={(e) => setWeekStart(e.target.value)}
            className="px-3 py-1.5 border border-slate-300 rounded-md text-sm"
            data-testid="confirm-week-date"
          />
          <p className="text-sm">
            Week summary: <span className="font-semibold">{summary.confirmed}/{summary.total}</span> confirmed
            {summary.pending > 0 && <span className="text-amber-700"> · {summary.pending} pending</span>}
            {summary.concerns > 0 && <span className="text-rose-700"> · {summary.concerns} concern{summary.concerns === 1 ? "" : "s"}</span>}
          </p>
        </div>
        {summary.pending > 0 && (
          <button
            onClick={confirmAll}
            disabled={busy}
            data-testid="confirm-week-button"
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-md font-medium text-sm"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCheck className="h-4 w-4" />} Confirm all shifts
          </button>
        )}
        {allConfirmed && (
          <span className="inline-flex items-center gap-1 text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-1.5 rounded-md">
            <Check className="h-3 w-3" /> All shifts confirmed
          </span>
        )}
      </div>
      {msg && (
        <p className="mt-2 text-xs bg-slate-50 border border-slate-200 text-slate-700 rounded px-2 py-1.5" data-testid="confirm-week-msg">
          {msg}
        </p>
      )}
    </div>
  );
}

function Kpi({ label, value, tone, testId }) {
  const toneCls =
    tone === "warn" ? "border-rose-200 bg-rose-50" : tone === "amber" ? "border-amber-200 bg-amber-50" : tone === "ok" ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-white";
  return (
    <div className={`rounded-md border ${toneCls} px-3 py-2`} data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
      <p className="text-lg font-bold text-slate-900 mt-0.5 tabular-nums">{value}</p>
    </div>
  );
}
